import { MantineTheme } from "@mantine/core";
import { Editor } from "grapesjs";
import { renderToStaticMarkup } from "react-dom/server";

export const CoreComponentsPlugin =
  (theme: MantineTheme) => (editor: Editor) => {
    const { Blocks } = editor;

    addWrapper(editor);
    addBox(editor);

    Blocks.add("text", {
      label: "Text",
      category: "Basic",
      activate: true,
      content: renderToStaticMarkup(<p>Text</p>),
    });

    Blocks.add("label", {
      label: "Label",
      category: "Basic",
      activate: true,
      content: renderToStaticMarkup(
        <p
          style={{
            fontSize: 12,
            color: theme.colors.gray[5],
          }}
        >
          Label
        </p>
      ),
    });

    Blocks.add("image", {
      label: "Image",
      category: "Basic",
      activate: true,
      content: renderToStaticMarkup(
        <img alt="image-alt" style={{ display: "block" }} />
      ),
    });

    addAnchor(editor);

    addResponsiveContainer(editor, theme);
    addAlign(editor);
    addGroup(editor);
    addSimpleGrid(editor);

    addLoremIpsumText(editor);
    // addArtistBio(editor, theme);
    // addHighlightComponents(editor, theme);
  };

const addWrapper = (editor: Editor) => {
  const { Components } = editor;
  const type = "hl-wrapper";

  const wrapperStyles = `
    .${type} {
      height: 100%;
      width: 100%;
    }
  `;

  Components.addType(type, {
    isComponent() {
      // No need to check since the element needs
      // to have the data-gjs-type attribute
      return false;
    },
    model: {
      defaults: {
        tagName: "div",
        name: "Wrapper",
        attributes: {
          class: type,
        },
        traits: [],
        droppable: ".box",
        styles: wrapperStyles,
        stylable: ["background-color"],
        removable: false,
        copyable: false,
        draggable: false,
        toolbar: [],
      },
    },
  });
};

const addResponsiveContainer = (editor: Editor, theme: MantineTheme) => {
  const { Components, Blocks } = editor;
  const type = "responsive-container";
  const componentStyles = `
    .${type} {
      margin: 0 auto;
      width: 100%;
      max-width: ${theme.breakpoints.xl}px;
      padding-right: 20px;
      padding-left: 20px;
    }
  
    @media (min-width: ${theme.breakpoints.sm}px) {
      .${type} {
        padding-right: 40px;
        padding-left: 40px;
      }
    }
    
    @media (min-width: ${theme.breakpoints.md}px) {
      .${type} {
        padding-right: 80px;
        padding-left: 80px;
      }
    }
  `;

  Components.addType(type, {
    isComponent(el) {
      if (el.tagName?.toLowerCase() === "div" && el.classList.contains(type)) {
        return { type };
      }
    },
    model: {
      defaults: {
        tagName: "div",
        name: "Responsive Container",
        attributes: {
          class: type,
        },
        traits: [],
        draggable: ".box",
        droppable: ".box",
        styles: componentStyles,
        stylable: ["height", "min-height"],
      },
    },
  });

  Blocks.add("container", {
    label: "Container",
    category: "Layout",
    activate: true,
    content: renderToStaticMarkup(<div className={type}></div>),
  });
};

const addBox = (editor: Editor) => {
  const { Components, Blocks } = editor;
  const type = "box";
  Components.addType(type, {
    isComponent(el) {
      if (el.tagName?.toLowerCase() === "div" && el.classList.contains(type)) {
        return { type };
      }
    },
    model: {
      defaults: {
        tagName: "div",
        name: "Box",
        attributes: {
          class: type,
        },
        traits: [],
        // unstylable: ["object-fit"],
      },
    },
  });

  Blocks.add(type, {
    label: "Box",
    category: "Basic",
    activate: true,
    content: renderToStaticMarkup(<div className={type}></div>),
  });
};

const addAnchor = (editor: Editor) => {
  const { Components, Blocks } = editor;
  const type = "box-anchor";
  Components.addType(type, {
    isComponent(el) {
      if (el.tagName?.toLowerCase() === "a" && el.classList.contains(type)) {
        return { type };
      }
    },
    model: {
      defaults: {
        tagName: "div",
        name: "Anchor",
        attributes: {
          href: "#",
          target: "_blank",
          class: type,
        },
        traits: [
          {
            type: "text",
            name: "href",
            default: "#",
            label: "Link",
          },
          {
            type: "select",
            name: "target",
            label: "Target",
            options: [
              { id: "_blank", label: "New tab" },
              { id: "_self", label: "Same tab" },
            ],
          },
        ],
        draggable: ".box",
        droppable: ".box",
        stylable: [],
        // unstylable: ["object-fit"],
      },
    },
  });

  Blocks.add(type, {
    label: "Anchor",
    category: "Basic",
    activate: true,
    content: renderToStaticMarkup(
      <a
        className={type}
        style={{
          display: "block",
          cursor: "pointer",
          textDecoration: "none",
          color: "#000000",
        }}
      ></a>
    ),
  });
};

const addAlign = (editor: Editor) => {
  const { Components, Blocks } = editor;
  const type = "align";

  Components.addType(type, {
    isComponent(el) {
      if (el.tagName?.toLowerCase() === "div" && el.classList.contains(type)) {
        return { type };
      }
    },
    model: {
      defaults: {
        tagName: "div",
        name: "Align",
        attributes: {
          class: type,
        },
        draggable: ".box",
        droppable: ".box",
        stylable: ["width", "height", "justify-content", "align-items"],
      },
    },
  });

  Blocks.add(type, {
    label: "Align",
    category: "Layout",
    activate: true,
    content: renderToStaticMarkup(
      <div
        className={type}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}
      ></div>
    ),
  });
};

const addGroup = (editor: Editor) => {
  const { StyleManager, Components, Blocks } = editor;
  const type = "group";

  StyleManager.addSector("group", {
    name: "Group",
    open: true,
    properties: [
      {
        id: "group-flex-direction",
        toRequire: true,
        type: "select",
        label: "Direction",
        property: "flex-direction",
        default: "row",
        options: [
          { id: "row", label: "Row" },
          { id: "column", label: "Column" },
        ],
        requires: {
          display: ["flex"],
        },
      },
      // {
      //   id: "group-gap",
      //   toRequire: true,
      //   type: "number",
      //   label: "Gap",
      //   property: "gap",
      //   unit: "px",
      //   units: ["px"],
      //   default: "0",
      //   min: 0,
      //   requires: {
      //     display: ["flex"],
      //   },
      // },
      {
        ...StyleManager.getBuiltIn("gap"),
        id: "group-gap",
        toRequire: true,
      },
      // Group justify content
      {
        id: "group-justify-content",
        type: "select",
        toRequire: true,
        label: "Distribute",
        property: "justify-content",
        default: "flex-start",
        options: [
          { id: "flex-start", label: "Start" },
          { id: "center", label: "Center" },
          { id: "flex-end", label: "End" },
          { id: "space-between", label: "Space between" },
          { id: "space-around", label: "Space around" },
          { id: "space-evenly", label: "Space evenly" },
        ],
        requires: {
          display: ["flex"],
        },
      },
      // Group align items
      {
        id: "group-align-items",
        type: "select",
        toRequire: true,
        label: "Align",
        property: "align-items",
        default: "stretch",
        options: [
          { id: "stretch", label: "Stretch" },
          { id: "flex-start", label: "Start" },
          { id: "center", label: "Center" },
          { id: "flex-end", label: "End" },
          { id: "baseline", label: "Baseline" },
        ],
        requires: {
          display: ["flex"],
        },
      },
    ],
  });

  Components.addType(type, {
    isComponent(el) {
      if (el.tagName?.toLowerCase() === "div" && el.classList.contains(type)) {
        return { type };
      }
    },
    model: {
      defaults: {
        tagName: "div",
        name: "Group",
        attributes: {
          class: type,
        },
        traits: [],
        draggable: ".box",
        droppable: ".box",
        stylable: ["width", "height"],
        "stylable-require": [
          "group-flex-direction",
          "group-gap",
          "group-justify-content",
          "group-align-items",
        ],
      },
    },
  });

  Blocks.add(type, {
    label: "Group",
    category: "Layout",
    activate: true,
    content: renderToStaticMarkup(
      <div className={type} style={{ display: "flex" }}>
        <div className="box"></div>
        <div className="box"></div>
      </div>
    ),
  });
};

const addSimpleGrid = (editor: Editor) => {
  const { Components, Blocks } = editor;
  const type = "simple-grid";

  Components.addType(type, {
    isComponent(el) {
      if (el.tagName?.toLowerCase() === "div" && el.classList.contains(type)) {
        return { type };
      }
    },
    model: {
      defaults: {
        tagName: "div",
        name: "Grid",
        attributes: {
          class: type,
        },
        traits: [],
        draggable: ".box",
        droppable: ".box",
        stylable: ["grid-template-columns", "column-gap", "row-gap"],
      },
    },
  });

  Blocks.add(type, {
    label: "Simple Grid",
    category: "Layout",
    activate: true,
    content: renderToStaticMarkup(
      <div
        className={type}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          columnGap: 24,
          rowGap: 24,
        }}
      >
        <div className="box"></div>
        <div className="box"></div>
        <div className="box"></div>
        <div className="box"></div>
        <div className="box"></div>
        <div className="box"></div>
      </div>
    ),
  });
};

const addLoremIpsumText = (editor: Editor) => {
  const { Blocks } = editor;

  Blocks.add("lorem-ipsum-text", {
    label: "Lorem ipsum text",
    category: "Advanced",
    activate: true,
    content: renderToStaticMarkup(
      <p>
        Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
        tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim
        veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea
        commodo consequat. Duis aute irure dolor in reprehenderit in voluptate
        velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint
        occaecat cupidatat non proident, sunt in culpa qui officia deserunt
        mollit anim id est laborum.
      </p>
    ),
  });

  console.log("addTextWithLorem");
};

// const addArtistBio = (editor: Editor, theme: MantineTheme) => {
//   const { Blocks } = editor;
//   Blocks.add("artist-bio", {
//     label: "Artist Bio",
//     category: "Demo",
//     activate: true,
//     attributes: {
//       blockDropStyles: {
//         rules: [
//           {
//             rule: ".id",
//             value: {
//               color: "blue",
//             },
//           },
//           {
//             rule: ".id",
//             atRuleType: "media",
//             atRuleParams: `(max-width: ${theme.breakpoints.lg - 1}px)`,
//             value: {
//               color: "red",
//             },
//           },
//           {
//             rule: ".id",
//             atRuleType: "media",
//             atRuleParams: `(max-width: ${theme.breakpoints.md - 1}px)`,
//             value: {
//               color: "green",
//             },
//           },
//         ],
//       },
//     },
//     content: renderToStaticMarkup(
//       <div
//         className="group"
//         style={{
//           display: "flex",
//           flexDirection: "column",
//           gap: "10px",
//         }}
//       >
//         <div
//           style={{
//             fontSize: 12,
//             color: theme.colors.gray[5],
//           }}
//         >
//           Artist
//         </div>
//         <div
//           style={{
//             fontSize: 15,
//             fontWeight: 500,
//           }}
//         >
//           Chuck Anderson
//         </div>
//         <div
//           style={{
//             lineHeight: "150%",
//           }}
//         >
//           Anderson is an artist and designer known for his digital art that
//           experiments with color and light. He founded NoPattern Studio in 2004
//           and has collaborated with brands such as ESPN, Nike, and Microsoft. In
//           2005, he co-founded the early internet blog THE BRILLIANCE! with
//           Benjamin Edgar and Virgil Abloh.
//           <br />
//           <br />
//           In 2021, Anderson worked with NFT platform Chain/Saw on his collection
//           INFINITE PRESSURE...
//         </div>

//         <a
//           href=""
//           style={{
//             fontSize: 14,
//             color: theme.colors.gray[5],
//           }}
//         >
//           View artist profile
//         </a>
//       </div>
//     ),
//   });
// };
